import type { SharedAccountItem, SharedSearchFolderItem } from '../useData';
import { isReceivedDatePreset, type ReceivedDatePreset } from './receivedDatePresets';

const STORAGE_KEY = 'ums-email-date-scope-v1';

export type EmailDateScopeStored =
  | { kind: 'any' }
  | { kind: 'preset'; p: ReceivedDatePreset }
  | { kind: 'year'; y: number };

type ScopeMap = Record<string, EmailDateScopeStored>;

function readMap(): ScopeMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as ScopeMap) : {};
  } catch {
    return {};
  }
}

export function loadEmailDateScopeForKey(mapKey: string): EmailDateScopeStored | null {
  const map = readMap();
  const v = map[mapKey];
  if (!v || typeof v !== 'object' || !('kind' in v)) return null;
  if (v.kind === 'any') return { kind: 'any' };
  if (v.kind === 'preset' && typeof v.p === 'string' && isReceivedDatePreset(v.p)) {
    return { kind: 'preset', p: v.p };
  }
  if (v.kind === 'year' && typeof v.y === 'number' && Number.isFinite(v.y)) return { kind: 'year', y: v.y };
  return null;
}

export function searchFolderPeriodToStored(
  periodPreset?: string | null,
  periodYear?: number | null,
): EmailDateScopeStored {
  if (periodYear != null && Number.isFinite(periodYear)) {
    return { kind: 'year', y: periodYear };
  }
  const preset = (periodPreset ?? '').trim().toLowerCase();
  if (isReceivedDatePreset(preset)) {
    return { kind: 'preset', p: preset };
  }
  return { kind: 'any' };
}

export function saveEmailDateScopeForKey(mapKey: string, scope: EmailDateScopeStored): void {
  try {
    const map = readMap();
    map[mapKey] = scope;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /**/
  }
}

/**
 * Stable key for persisting date filter (year / recent preset) per navigation context.
 */
export function buildEmailDateScopeMapKey(params: {
  specialView: import('./emailSidebarSelectionStorage').EmailSpecialView | null;
  selectedShareId?: number;
  selectedPersonalFolderId?: number;
  selectedSearchFolderId?: number;
  selectedSharedSearchFolder: SharedSearchFolderItem | null;
  selectedAccount?: number;
  selectedSharedAccount: SharedAccountItem | null;
  selectedSharedAccountFolder: string;
  selectedTagId?: number;
}): string | null {
  if (params.specialView === 'drafts' || params.specialView === 'templates' || params.specialView === 'outbox') {
    return null;
  }
  if (params.specialView === 'mySent') {
    return 'special:mySent';
  }
  if (params.specialView === 'archived') {
    return 'special:archived';
  }

  let scope = 'all';
  if (params.selectedShareId != null) scope = `shf:${params.selectedShareId}`;
  else if (params.selectedPersonalFolderId != null) scope = `pf:${params.selectedPersonalFolderId}`;
  else if (params.selectedSearchFolderId != null) scope = `sf:${params.selectedSearchFolderId}`;
  else if (params.selectedSharedSearchFolder) scope = `ssf:${params.selectedSharedSearchFolder.shareId}`;
  else if (params.selectedSharedAccount)
    scope = `sacc:${params.selectedSharedAccount.shareId}:${params.selectedSharedAccountFolder}`;
  else if (params.selectedAccount != null) scope = `acc:${params.selectedAccount}`;
  else scope = 'all';

  const tagPart = params.selectedTagId != null ? `:t:${params.selectedTagId}` : '';
  return `${scope}${tagPart}`;
}
